import type { CreateCurbAreaInput, DeleteCurbAreaMutationVariables, UpdateCurbAreaInput } from "@/types/gql/graphql";
import {
  QUERY_ADD_CURB_AREA,
  QUERY_CURB_AREAS,
  QUERY_REMOVE_CURB_AREA,
  QUERY_UPDATE_CURB_AREA,
} from "@/services/web/api-curb-area.const";
import { hideGlobalLoading, showGlobalLoading } from "@/hooks/use-global-loading";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";
import { Feature } from "geojson";

export const useQueryCurbArea = () => {
  const [isResetData, setIsResetData] = useState<boolean>(false);

  const { currentCity } = useContext(CityContext);

  const variables = useMemo(() => {
    return {
      cityId: currentCity?.id ?? "",
    };
  }, [currentCity]);

  const refetchQueriesConfig = useMemo(() => {
    return [{ query: QUERY_CURB_AREAS, variables }];
  }, [variables]);

  const { data, loading } = useTrackedQuery(QUERY_CURB_AREAS, {
    variables,
    skip: !currentCity?.id,
    actionName: "QueryCurbAreas",
  });

  useEffect(() => {
    if (loading) {
      showGlobalLoading();
    } else {
      hideGlobalLoading();
    }
  }, [loading]);

  const curbAreas = useMemo(() => {
    if (data?.curbAreas) {
      const curbAreaFeatures: Feature[] = data.curbAreas.map((area) => {
        return {
          type: "Feature" as const,
          properties: {},
          ...area,
        };
      });
      return curbAreaFeatures;
    }
    return [];
  }, [data]);

  const resetState = () => {
    setIsResetData(true);
  };

  const [createCurbArea] = useTrackedMutation(QUERY_ADD_CURB_AREA, {
    actionName: "QueryCreateCurbArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const [updateCurbArea] = useTrackedMutation(QUERY_UPDATE_CURB_AREA, {
    actionName: "QueryUpdateCurbArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const [removeCurbArea] = useTrackedMutation(QUERY_REMOVE_CURB_AREA, {
    actionName: "QueryRemoveCurbArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const handleAddCurbArea = async (input: CreateCurbAreaInput) => {
    const response = await createCurbArea({ variables: { input } });
    return response.data;
  };

  const handleUpdateCurbArea = async (input: UpdateCurbAreaInput) => {
    const response = await updateCurbArea({ variables: { input } });
    return response.data;
  };

  const handleRemoveCurbArea = async (input: DeleteCurbAreaMutationVariables) => {
    const response = await removeCurbArea({ variables: input });
    return response.data;
  };
  return {
    curbAreas,
    loading,
    currentCity,
    handleAddCurbArea,
    handleUpdateCurbArea,
    handleRemoveCurbArea,
    isResetData,
    setIsResetData,
  };
};
